// <installer-map> — certified installer map for /pages/pro-installation.
// ESM module: pure helpers are exported for node --test; the custom element
// defines itself only in a browser. No build step — loaded via asset_url.
// Contract: docs/contracts/installer-map.v1.md

export const TIER_ORDER = ['elite', 'preferred', 'pro'];

// Irrigreen brand palette (sourced from config/settings_data.json):
// #f0c417 checkout accent (yellow), #1f4d3a secondary button / rating (deep
// green), #fffcf5 cream. Pro is a sage tint of the brand green so Preferred
// and Pro read as one hierarchy while Elite pops in the accent yellow.
// These are only DEFAULTS — the live values are section settings, so they can
// be retuned in the theme editor without touching this file.
export const DEFAULT_TIER_COLORS = {
  elite: '#f0c417',
  preferred: '#1f4d3a',
  pro: '#7fa891'
};

// Tiers that must never be hidden inside a cluster bubble. Elite is the whole
// point of the tiering, and at national zoom clustering otherwise swallows all
// ~15 of them — so they always render as individual pins, slightly larger and
// above everything else. Add a tier here to give it the same treatment.
export const ALWAYS_VISIBLE_TIERS = new Set(['elite']);

const PIN_SCALE = 1.4;
const ALWAYS_VISIBLE_PIN_SCALE = 1.8;

// Cluster bubbles: brand deep green with cream text, replacing the clusterer's
// default blue/red, which read as off-brand alert colors on a cream page.
const CLUSTER_FILL = '#1f4d3a';
const CLUSTER_TEXT = '#fffcf5';

// How many sidebar cards to render at once. Whatever is dropped is reported in
// the status line — never truncate silently.
const RESULTS_LIMIT = 30;

// Zoom applied when the visitor picks one installer. Close enough to read the
// surrounding town, but we never zoom *out* — if they've already zoomed further
// in, respect it.
export const FOCUS_ZOOM = 11;

export function zoomForFocus(currentZoom, target = FOCUS_ZOOM) {
  return typeof currentZoom === 'number' && currentZoom > target ? currentZoom : target;
}

// Sidebar ordering: nearest-first, except the installer the visitor just picked,
// which is lifted to the top so it stays visible after the map re-centres.
export function orderResults(models, center, pinnedId) {
  const ranked = sortByDistance(models, center);
  if (pinnedId == null) return ranked;
  const i = ranked.findIndex((m) => String(m.id) === String(pinnedId));
  if (i <= 0) return ranked;
  return [ranked[i], ...ranked.slice(0, i), ...ranked.slice(i + 1)];
}

const TIER_LABELS = { elite: 'Elite', preferred: 'Preferred', pro: 'Pro' };

export function tierLabel(tier) {
  return TIER_LABELS[tier] || 'Pro';
}

export function tierColor(tier, colors = DEFAULT_TIER_COLORS) {
  return colors[tier] || DEFAULT_TIER_COLORS[tier] || DEFAULT_TIER_COLORS.pro;
}

function locationText(city, state) {
  return [city, state].filter(Boolean).join(', ');
}

// Projects a contract Installer into the marker view model. Explicit
// allowlist — any extra keys (e.g. email/address) are dropped, not spread.
export function toMarkerModel(installer, colors = DEFAULT_TIER_COLORS) {
  const tier = TIER_ORDER.includes(installer.tier) ? installer.tier : 'pro';
  return {
    id: String(installer.id),
    name: installer.name,
    tier,
    color: tierColor(tier, colors),
    position: { lat: Number(installer.lat), lng: Number(installer.lng) },
    locationText: locationText(installer.city, installer.state),
    phone: installer.phone ?? null,
    website: installer.website ?? null
  };
}

export function filterByTiers(models, activeTiers) {
  return models.filter((m) => activeTiers.has(m.tier));
}

export function haversineMiles(a, b) {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export function sortByDistance(models, center) {
  return [...models].sort(
    (m1, m2) => haversineMiles(m1.position, center) - haversineMiles(m2.position, center)
  );
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Reject javascript:, data:, vbscript:, etc. — only http(s) links are safe to
// render as anchors in the Maps InfoWindow.
function isSafeHttpUrl(s) {
  try { return /^https?:$/i.test(new URL(s).protocol); } catch { return false; }
}

// Contact details are content, not just controls — render the real number so it
// can be read, copied, or dialled, rather than hiding it behind a "Call" button.
// The tel: href keeps the raw value; only the visible label is prettified.
export function formatPhone(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) return String(raw ?? '').trim();
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

// Show the domain rather than a generic "Visit website" label — it tells the
// visitor who they're about to click through to.
export function websiteLabel(url) {
  try { return new URL(url).hostname.replace(/^www\./i, ''); } catch { return String(url ?? ''); }
}

// Shared contact block for both the map popup and the sidebar card, so the two
// surfaces can never drift apart on what they expose.
function contactHtml(m) {
  const items = [];
  if (m.phone) {
    items.push(
      `<li><a class="im-contact__link" href="tel:${esc(m.phone)}">${esc(formatPhone(m.phone))}</a></li>`
    );
  }
  if (m.website && isSafeHttpUrl(m.website)) {
    items.push(
      `<li><a class="im-contact__link im-contact__link--web" href="${esc(m.website)}" target="_blank" rel="noopener">${esc(websiteLabel(m.website))}</a></li>`
    );
  }
  return items.length ? `<ul class="im-contact">${items.join('')}</ul>` : '';
}

function tierBadge(tier) {
  return `<span class="im-badge im-badge--${esc(tier)}">${esc(tierLabel(tier))}</span>`;
}

export function popupHtml(m) {
  const loc = m.locationText ? `<p class="im-pop__loc">${esc(m.locationText)}</p>` : '';
  return (
    `<article class="im-pop">` +
    `<button type="button" class="im-pop__close" aria-label="Close">&times;</button>` +
    `<div class="im-pop__head">${tierBadge(m.tier)}` +
    `<h3 class="im-pop__name">${esc(m.name)}</h3>${loc}</div>` +
    contactHtml(m) +
    `</article>`
  );
}

// Sidebar result card. `distanceText` is optional — only shown once the visitor
// has anchored the map to a place (search or geolocation).
export function resultCardHtml(m, distanceText) {
  const dist = distanceText ? `<span class="im-card__dist">${esc(distanceText)}</span>` : '';
  const loc = m.locationText ? `<p class="im-card__loc">${esc(m.locationText)}</p>` : '';
  return (
    `<li class="im-card" data-id="${esc(m.id)}">` +
    `<button type="button" class="im-card__focus" aria-label="Show ${esc(m.name)} on the map">` +
    `<span class="im-card__meta">${tierBadge(m.tier)}${dist}</span>` +
    `<span class="im-card__name">${esc(m.name)}</span>${loc}</button>` +
    contactHtml(m) +
    `</li>`
  );
}

// ---- Browser-only custom element ----------------------------------------
// Guarded so `import`ing this module under node (for unit tests) is a no-op.
if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
  const US_CENTER = { lat: 39.5, lng: -98.35 };

  // Any Maps JS API loader, whoever added it — see loadMaps below.
  const MAPS_LOADER_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]';
  const mapsReady = () => !!(window.google && window.google.maps && window.google.maps.Map);

  // Poll rather than rely on a callback we may not own.
  function waitForMaps(timeoutMs = 20000, stepMs = 100) {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const tick = () => {
        if (mapsReady()) return resolve();
        if (Date.now() - started > timeoutMs) {
          return reject(new Error('Google Maps did not become available'));
        }
        setTimeout(tick, stepMs);
      };
      tick();
    });
  }

  class InstallerMap extends HTMLElement {
    connectedCallback() {
      this.colors = {
        elite: this.dataset.colorElite || DEFAULT_TIER_COLORS.elite,
        preferred: this.dataset.colorPreferred || DEFAULT_TIER_COLORS.preferred,
        pro: this.dataset.colorPro || DEFAULT_TIER_COLORS.pro
      };
      this.activeTiers = new Set(TIER_ORDER);
      this.models = [];
      this.markers = [];
      this.renderChrome();
      this.loadMaps()
        .then(() => this.fetchData())
        .then(() => this.initMap())
        .catch((err) => this.showError(err));
    }

    renderChrome() {
      this.innerHTML = `
        <div class="im">
          <div class="im-find">
            <button type="button" class="im-locate">Use my location</button>
            <form class="im-search"><input type="search" name="q" placeholder="Or enter a zip code or city"
              aria-label="Search installers near a location"><button type="submit">Search</button></form>
          </div>
          <div class="im-legend">${TIER_ORDER.map((t) => `
            <label class="im-legend__item"><input type="checkbox" value="${t}" checked>
              <span class="im-dot" style="background:${this.colors[t]}"></span>${tierLabel(t)}</label>`).join('')}
          </div>
          <div class="im-layout">
            <div class="im-canvas" role="application" aria-label="Map of certified installers"></div>
            <aside class="im-results" aria-label="Installer details">
              <p class="im-status" role="status"></p>
              <ol class="im-results__list"></ol>
            </aside>
          </div>
        </div>`;
      this.canvas = this.querySelector('.im-canvas');
      this.status = this.querySelector('.im-status');
      this.resultsList = this.querySelector('.im-results__list');
      this.resultsList.addEventListener('click', (e) => {
        // Let the phone / website links inside a card do their own thing.
        if (e.target.closest('a')) return;
        const card = e.target.closest('.im-card');
        if (card) this.focusInstaller(card.dataset.id);
      });
      this.querySelector('.im-search').addEventListener('submit', (e) => {
        e.preventDefault();
        this.search(new FormData(e.target).get('q'));
      });
      this.querySelector('.im-locate').addEventListener('click', () => this.locateVisitor());
      this.querySelectorAll('.im-legend input').forEach((cb) =>
        cb.addEventListener('change', () => {
          if (cb.checked) this.activeTiers.add(cb.value);
          else this.activeTiers.delete(cb.value);
          this.renderMarkers();
        })
      );
    }

    // Resolves once google.maps is usable. Deliberately tolerant of OTHER
    // loaders on the page: snippets/sections unrelated to this element (the
    // address-autocomplete block in video.liquid / rich-text.liquid) load the
    // Maps API with their own &libraries=places URL. If we inject a second
    // loader while theirs is in flight, Google logs "included multiple times"
    // and does NOT invoke our callback — so our promise never settles and the
    // map silently never initialises. That is a race we lose about half the
    // time; it took down the live map on 2026-07-24. Wait for an in-flight
    // loader instead of adding a duplicate.
    loadMaps() {
      if (mapsReady()) return Promise.resolve();
      if (document.querySelector(MAPS_LOADER_SELECTOR)) return waitForMaps();
      const key = this.dataset.mapsKey;
      if (!key) return Promise.reject(new Error('Missing data-maps-key'));
      return new Promise((resolve, reject) => {
        // Use a unique callback name per instance so multiple <installer-map>
        // elements on the same page don't clobber each other's callback.
        const cbName = `__installerMapReady_${(window.__installerMapSeq = (window.__installerMapSeq || 0) + 1)}`;
        const s = document.createElement('script');
        s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&callback=${cbName}`;
        s.async = true;
        window[cbName] = () => { delete window[cbName]; resolve(); };
        s.onerror = () => reject(new Error('Google Maps failed to load'));
        document.head.appendChild(s);
        // Belt and braces: if a competing loader lands between our check above
        // and this insertion, our callback may never fire. Fall back to polling
        // so the map still comes up instead of hanging forever.
        waitForMaps().then(resolve, () => {});
      });
    }

    async fetchData() {
      const res = await fetch(this.dataset.endpoint, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`Installer feed ${res.status}`);
      const data = await res.json();
      this.models = (data.installers || [])
        .filter((i) => Number.isFinite(Number(i.lat)) && Number.isFinite(Number(i.lng)))
        .map((i) => toMarkerModel(i, this.colors));
    }

    initMap() {
      const center = this.parseCenter();
      this.map = new google.maps.Map(this.canvas, {
        center, zoom: Number(this.dataset.defaultZoom) || 4,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false
      });
      // headerDisabled removes Google's default header + close button, whose
      // padding is what made the old popup look lopsided. We render our own
      // close control instead and own the whole surface.
      this.info = new google.maps.InfoWindow({ headerDisabled: true });
      this.geocoder = new google.maps.Geocoder();
      this.renderMarkers();
      // Re-rank the sidebar as the visitor pans/zooms, so it always describes
      // what they're looking at.
      this.map.addListener('idle', () => this.renderResults());
      this.autoLocate();
    }

    renderMarkers() {
      if (this.cluster) this.cluster.clearMarkers();
      this.markers.forEach((m) => m.setMap(null));
      const visible = filterByTiers(this.models, this.activeTiers);
      this.markerById = new Map();
      const alwaysVisible = [];
      const clusterable = [];
      this.markers = visible.map((model) => {
        const pinned = ALWAYS_VISIBLE_TIERS.has(model.tier);
        const marker = new google.maps.Marker({
          position: model.position,
          title: model.name,
          icon: this.pinIcon(model.color, pinned ? ALWAYS_VISIBLE_PIN_SCALE : PIN_SCALE),
          zIndex: pinned ? 3 : 1
        });
        marker.addListener('click', () => this.focusOn(model, marker));
        this.markerById.set(model.id, { marker, model });
        (pinned ? alwaysVisible : clusterable).push(marker);
        return marker;
      });
      alwaysVisible.forEach((m) => m.setMap(this.map));
      if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
        this.cluster = new window.markerClusterer.MarkerClusterer({
          map: this.map, markers: clusterable, renderer: this.clusterRenderer()
        });
      } else {
        clusterable.forEach((m) => m.setMap(this.map));
      }
      this.renderResults();
    }

    // Brand-coloured cluster bubbles. The clusterer's stock renderer paints
    // blue/red circles that read as alert states against the cream page.
    clusterRenderer() {
      return {
        render: ({ count, position }) => new google.maps.Marker({
          position,
          zIndex: 2,
          label: { text: String(count), color: CLUSTER_TEXT, fontSize: '12px', fontWeight: '600' },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: CLUSTER_FILL, fillOpacity: 1,
            strokeColor: '#ffffff', strokeWeight: 2,
            scale: count < 10 ? 15 : count < 50 ? 19 : 23
          }
        })
      };
    }

    openPopup(model, marker) {
      // Build as a node (not a string) so our close button can be wired up
      // directly; popupHtml itself stays pure for unit tests.
      const wrap = document.createElement('div');
      wrap.innerHTML = popupHtml(model);
      const close = wrap.querySelector('.im-pop__close');
      if (close) close.addEventListener('click', () => this.info.close());
      this.info.setContent(wrap);
      if (marker.getMap && marker.getMap()) {
        this.info.open({ map: this.map, anchor: marker });
      } else {
        // Reached from a sidebar card whose marker is currently collapsed into a
        // cluster: it has no map, so it cannot anchor an InfoWindow. Open at its
        // coordinates instead, otherwise the popup silently fails to appear.
        this.info.setPosition(marker.getPosition());
        this.info.open({ map: this.map });
      }
    }

    // Single entry point for "the visitor picked this installer", whether they
    // clicked the pin or the sidebar card: zoom in on it, open its popup, and
    // lift it to the top of the list so it stays visible after the map moves.
    focusOn(model, marker) {
      this.pinnedId = model.id;
      this.map.panTo(marker.getPosition());
      this.map.setZoom(zoomForFocus(this.map.getZoom()));
      this.openPopup(model, marker);
      this.renderResults();
    }

    // Sidebar: nearest-first relative to whatever the map is currently centred
    // on, capped for rendering cost. The cap is stated in the status line — a
    // silent truncation would read as "these are all of them".
    renderResults() {
      if (!this.resultsList || !this.map) return;
      const visible = filterByTiers(this.models, this.activeTiers);
      const centerObj = this.origin || this.map.getCenter();
      if (!centerObj) return;
      const center = typeof centerObj.lat === 'function'
        ? { lat: centerObj.lat(), lng: centerObj.lng() }
        : centerObj;
      const ranked = orderResults(visible, center, this.pinnedId);
      const shown = ranked.slice(0, RESULTS_LIMIT);
      this.resultsList.innerHTML = shown
        .map((m) => resultCardHtml(m, this.origin ? `${Math.round(haversineMiles(m.position, center))} mi` : ''))
        .join('');
      const scope = this.originLabel ? ` near ${this.originLabel}` : '';
      this.status.textContent = visible.length > shown.length
        ? `Nearest ${shown.length} of ${visible.length} certified installers${scope}`
        : `${visible.length} certified installer${visible.length === 1 ? '' : 's'}${scope}`;
      // Re-applied here rather than in openPopup: renderResults replaces the
      // list's innerHTML, so any earlier highlight would be wiped.
      this.markPinnedCard();
    }

    markPinnedCard() {
      if (!this.resultsList || this.pinnedId == null) return;
      const card = this.resultsList.querySelector(`.im-card[data-id="${CSS.escape(String(this.pinnedId))}"]`);
      if (!card) return;
      this.resultsList.querySelectorAll('.im-card').forEach((el) => el.classList.remove('is-active'));
      card.classList.add('is-active');
      this.resultsList.scrollTop = 0;
    }

    focusInstaller(id) {
      const entry = this.markerById && this.markerById.get(id);
      if (entry) this.focusOn(entry.model, entry.marker);
    }

    pinIcon(color, scale = PIN_SCALE) {
      return {
        path: 'M12 0C6.5 0 2 4.5 2 10c0 7 10 18 10 18s10-11 10-18C22 4.5 17.5 0 12 0z',
        fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 1.5,
        scale, anchor: new google.maps.Point(12, 28)
      };
    }

    search(q) {
      if (!q || !this.geocoder) return;
      this.geocoder.geocode({ address: q, componentRestrictions: { country: 'US' } }, (results, statusCode) => {
        if (statusCode !== 'OK' || !results[0]) { this.status.textContent = 'Location not found'; return; }
        const loc = results[0].geometry.location;
        this.originLabel = String(q).trim();
        this.centerOn({ lat: loc.lat(), lng: loc.lng() }, 9);
      });
    }

    // Recenter + zoom the map, then report the nearest visible installer.
    centerOn(center, zoom) {
      if (!this.map) return null;
      // A new search/locate supersedes whichever pin was lifted to the top.
      this.pinnedId = null;
      // Anchor the sidebar's distance column to the place the visitor asked
      // about, rather than to wherever the map drifts afterwards.
      this.origin = center;
      this.map.setCenter(center);
      this.map.setZoom(zoom);
      this.renderResults();
      return sortByDistance(filterByTiers(this.models, this.activeTiers), center)[0] || null;
    }

    // Ask the visitor for their location (browser permission prompt); on grant,
    // zoom the map to them and surface the nearest installer.
    // `auto` = we started this ourselves rather than the visitor clicking, so
    // failure must stay quiet: an unprompted "could not get your location"
    // reads as a broken page when the visitor never asked.
    locateVisitor({ auto = false } = {}) {
      if (!('geolocation' in navigator)) {
        if (!auto) this.status.textContent = 'Location is not available in this browser.';
        return;
      }
      this.setLocating(true);
      if (!auto) this.status.textContent = 'Locating you...';
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.setLocating(false);
          this.originLabel = 'you';
          this.centerOn({ lat: pos.coords.latitude, lng: pos.coords.longitude }, 10);
        },
        () => {
          this.setLocating(false);
          if (!auto) this.status.textContent = 'Could not get your location - showing all installers.';
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
      );
    }

    setLocating(on) {
      const btn = this.querySelector('.im-locate');
      if (!btn) return;
      btn.classList.toggle('is-busy', !!on);
      btn.disabled = !!on;
      btn.textContent = on ? 'Locating…' : 'Use my location';
    }

    // "Near me" is the default view, but we don't fire an unsolicited OS
    // permission prompt on every page load. If permission was already granted
    // we locate silently; if it's still unasked we ask once (opt out via the
    // section's "Locate visitor automatically" setting); if it was denied we
    // never nag — the zip search stays as the way in.
    async autoLocate() {
      if (this.dataset.autoLocate === 'false') return;
      if (!('geolocation' in navigator)) return;
      let state = 'prompt';
      try {
        if (navigator.permissions && navigator.permissions.query) {
          state = (await navigator.permissions.query({ name: 'geolocation' })).state;
        }
      } catch {
        // Older Safari has no Permissions API for geolocation — treat as unasked.
      }
      if (state === 'denied') return;
      this.locateVisitor({ auto: true });
    }

    parseCenter() {
      const raw = (this.dataset.defaultCenter || '').split(',').map(Number);
      return raw.length === 2 && raw.every(Number.isFinite) ? { lat: raw[0], lng: raw[1] } : US_CENTER;
    }

    showError(err) {
      if (this.status) this.status.textContent = 'The installer map is temporarily unavailable.';
      console.error('[installer-map]', err);
    }
  }

  if (!customElements.get('installer-map')) customElements.define('installer-map', InstallerMap);
}
